import { useMemo } from "react";
import { getPortfolioReportingSummaryQuery } from "web_myplann/Api/Portfolio/Queries/getPortfolioReportingSummaryQuery";
import type { PortfolioListReportingSummaryResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { PORTFOLIO_SORT } from "web_myplann/Portfolio/Components/Redesign/Filter/portfolioSortKey";
import { getReportingPortfolioIds } from "web_myplann/Portfolio/Helpers/portfolioReportingDomains";
import { useContractStateLists } from "web_myplann/Portfolio/Hooks/useContractStateLists";
import { useEffectivePortfolioFilter } from "web_myplann/Portfolio/Hooks/useEffectivePortfolioFilter";
import { usePortfolioCategories } from "web_myplann/Portfolio/Hooks/usePortfolioCategories";
import type {
	CategoryItem,
	PortfolioCategory,
} from "web_myplann/Portfolio/Types/portfolioCategory";
import type { PortfolioFilterState } from "web_myplann/Shared/State/portfolioFilterAtom";
import { sumGroupItems } from "web_myplann/Shared/Utils/sumGroupItems";

type PortfolioListData = {
	categories: PortfolioCategory[];
	filter: PortfolioFilterState;
	totalItems: number;
	archivedItems: CategoryItem[];
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined;
	isLoading: boolean;
	hasNoData: boolean;
};

export const usePortfolioListData = (): PortfolioListData => {
	const { filter } = useEffectivePortfolioFilter();

	const {
		isArchivedView,
		portfolioItems: listedPortfolioItems,
		propertyItems: listedPropertyItems,
		isLoading,
		isSuccess,
	} = useContractStateLists();

	const portfolioItems = useMemo(
		() =>
			isArchivedView
				? listedPortfolioItems
				: listedPortfolioItems.filter((item) => !item.archived),
		[listedPortfolioItems, isArchivedView],
	);
	const propertyItems = useMemo(
		() =>
			isArchivedView
				? listedPropertyItems
				: listedPropertyItems.filter((item) => !item.archived),
		[listedPropertyItems, isArchivedView],
	);

	const archivedPortfolioItems = useMemo(
		() =>
			isArchivedView
				? []
				: listedPortfolioItems.filter((item) => item.archived),
		[listedPortfolioItems, isArchivedView],
	);
	const archivedPropertyItems = useMemo(
		() =>
			isArchivedView ? [] : listedPropertyItems.filter((item) => item.archived),
		[listedPropertyItems, isArchivedView],
	);

	const reportingIds = useMemo(
		() => getReportingPortfolioIds(portfolioItems),
		[portfolioItems],
	);
	const { data: reportingSummary } =
		getPortfolioReportingSummaryQuery(reportingIds);
	const summaryData = reportingSummary?.data;

	const selectedCategories = useMemo(
		() =>
			filter.categories.length > 0 ? new Set(filter.categories) : undefined,
		[filter.categories],
	);

	const isOblastSort = filter.sort === PORTFOLIO_SORT.oblast;

	const categoryOptions = {
		categories: selectedCategories,
		sort: filter.sort,
		sortDesc: isOblastSort ? false : filter.sortDesc,
		summaryData,
	};

	const sortedCategories = usePortfolioCategories(
		portfolioItems,
		propertyItems,
		categoryOptions,
	);

	const archivedCategories = usePortfolioCategories(
		archivedPortfolioItems,
		archivedPropertyItems,
		categoryOptions,
	);

	const archivedItems = useMemo<CategoryItem[]>(
		() => archivedCategories.flatMap((category) => category.items),
		[archivedCategories],
	);

	const categories = useMemo(
		() =>
			isOblastSort && filter.sortDesc
				? [...sortedCategories].reverse()
				: sortedCategories,
		[sortedCategories, isOblastSort, filter.sortDesc],
	);

	const totalItems = useMemo(() => sumGroupItems(categories), [categories]);

	const hasNoData =
		!isArchivedView &&
		isSuccess &&
		listedPortfolioItems.length === 0 &&
		listedPropertyItems.length === 0;

	return {
		categories,
		filter,
		totalItems,
		archivedItems,
		summaryData,
		isLoading,
		hasNoData,
	};
};
